import useUserStore, { useIndexStore } from "@/state/user/useUserStore";
import React, { useEffect, useRef, useState } from "react";
import { m } from "motion/react";
import {
  ArrowBigLeft,
  Brush,
  FlipHorizontal2,
  ImagePlus,
  Layers,
  Sparkle,
  Type,
  Upload,
} from "lucide-react";
import DrawComponent from "@/components/ui/draw-component";
import {
  CustomTab,
  TitleShadow,
  TitleShadowBullas,
  TitleShadowHenlo,
  TitleShadowHenlo2,
  UploadImageContainer,
} from "@/components/styles";
import AssetComponent from "@/components/ui/asset-component";
import MemeCanvas from "@/components/ui/meme-canvas";
import { Asset } from "@/types/types";
import Popup from "@/components/ui/download-modal";
import { domToPng } from "modern-screenshot";
import { useBackHandler } from "@/lib/hooks/useBackHanlder";
import Link from "next/link";
import LayersComponent from "@/components/ui/layers-component";
import TextComponent from "@/components/ui/text-component";
import UploadComponent from "@/components/ui/upload-component";

export const PostPage = ({
  color,
  assets,
  customFont,
  twitter,
  defaultImg,
  tweetText,
  websiteLink,
  replyTo,
  noShare,
}: {
  color: string;
  assets: Asset[];
  customFont?: string;
  twitter?: string;
  defaultImg?: string;
  tweetText?: string;
  websiteLink?: string;
  replyTo?: string;
  noShare?: boolean;
}) => {
  const [
    setSelectedImage,
    setAssets,
    textFields,
    removeTextField,
    flipImage,
    setFlipImage,
    history,
    setHistory,
    step,
    setStep,
    setUploadAssets,
    canvasIsLoading,
    uploadAssets,
    selectedAssets,
    setRestrictedFonts,
  ] = useUserStore((state) => [
    state.setSelectedImage,
    state.setAssets,
    state.textFields,
    state.removeTextField,
    state.flipImage,
    state.setFlipImage,
    state.history,
    state.setHistory,
    state.step,
    state.setStep,
    state.setUploadAssets,
    state.canvasIsLoading,
    state.uploadAssets,
    state.assets,
    state.setRestrictedFonts,
  ]);

  const [lastUploadAssetIndex, setLastUploadAssetIndex] = useIndexStore(
    (state) => [state.lastUploadAssetIndex, state.setLastUploadAssetIndex]
  );

  const divRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const parentVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.4,
        delayChildren: 0.3,
        staggerChildren: 0.3,
        type: "bounce",
      },
    },
  };

  const childVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
  };

  const undo = () => {
    if (step > 0) {
      setStep(step - 1); // Move the step back
      restoreState(history[step - 1]); // Restore the canvas to the previous state
    }
  };

  const redo = () => {
    if (step < history.length - 1) {
      setStep(step + 1); // Move the step forward
      restoreState(history[step + 1]); // Restore the canvas to the next state
    }
  };

  const restoreState = (canvasDataUrl: string | null) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        // Clear the canvas
        context.clearRect(0, 0, canvas.width, canvas.height);

        if (canvasDataUrl) {
          const img = new Image();
          img.onload = () => {
            // Get the device pixel ratio
            const dpr = window.devicePixelRatio || 1;

            // Calculate the scaled dimensions
            const scaledWidth = canvas.width / dpr;
            const scaledHeight = canvas.height / dpr;

            // Draw the image at the correct scale
            context.drawImage(img, 0, 0, scaledWidth, scaledHeight);
          };
          img.src = canvasDataUrl;
        }
      }
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext("2d");
      if (context) {
        context.clearRect(0, 0, canvas.width, canvas.height);
        setStep(0);
        setHistory([null]);
      }
    }
  };

  const [mode, setMode] = useState<
    "asset" | "draw" | "text" | "upload" | "layers"
  >("asset");
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setIsPopupOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupRef]);

  const [imageSrc, setImageSrc] = useState<string | null>(null); // State to hold the image source URL
  const [imageSrcIsLoading, setImageSrcIsLoading] = useState<boolean>(false);

  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  const convertToPngAndDisplay = async () => {
    if (divRef.current !== null) {
      setImageSrcIsLoading(true);
      try {
        const dataUrl = await domToPng(divRef.current, {
          scale: 2,
          backgroundColor: null,
        });
        setImageSrc(dataUrl);
      } catch (error) {
        console.error("Error capturing image:", error);
      }
      setImageSrcIsLoading(false);
    }
  };

  useEffect(() => {
    // Set restricted fonts for Henlo/blank page
    if (twitter === "henlo") {
      setRestrictedFonts([
        { value: "title13", label: "Henlo Clash" },
        { value: "title14", label: "Inter" },
        { value: "title15", label: "Inter Bold" },
      ]);
    }
  }, [twitter, setRestrictedFonts]);

  useEffect(() => {
    if (defaultImg) {
      setSelectedImage(defaultImg);
    }
  }, []);

  const backHandler = useBackHandler();

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        position: "relative",
      }}
    >
      {isPopupOpen && (
        <Popup
          popupRef={popupRef}
          imageSrc={imageSrc}
          color={color}
          memeRef={divRef}
          twitter={twitter ?? "0xhoneyjar"}
          setIsPopupOpen={setIsPopupOpen}
          customFont={customFont}
          isLoading={imageSrcIsLoading}
          tweetText={tweetText}
          websiteLink={websiteLink}
          replyTo={replyTo}
          noShare={noShare}
        />
      )}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          padding: "36px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginBottom: "16px",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "start",
              justifyContent: "center",
              flexDirection: "column",
            }}
          >
            {customFont === "henlo" && (
              <TitleShadowHenlo style={{ margin: 0, marginBottom: "4px" }}>
                Customize
              </TitleShadowHenlo>
            )}
            {customFont === "henlo2" && (
              <TitleShadowHenlo2 style={{ margin: 0, marginBottom: "4px" }}>
                Customize
              </TitleShadowHenlo2>
            )}
            {customFont === "bullas" && (
              <TitleShadowBullas style={{ margin: 0, marginBottom: "4px" }}>
                Customize
              </TitleShadowBullas>
            )}
            {!customFont && (
              <TitleShadow style={{ margin: 0, marginBottom: "4px" }}>
                Customize
              </TitleShadow>
            )}
            <p
              style={{
                margin: 0,
                fontFamily: "var(--font-cabin-condensed)",
              }}
            >
              Click on assets to add them to your image. Drag added assets
              around to position them. Hover on added assets and click the X to
              remove them.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 0,
            }}
          >
            {!defaultImg ? (
              <div
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontFamily: "var(--font-cabin-condensed)",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  color: "black",
                }}
                onClick={backHandler}
              >
                <UploadImageContainer
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                >
                  <ArrowBigLeft size={20} />
                  Back
                </UploadImageContainer>
              </div>
            ) : (
              <Link
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontFamily: "var(--font-cabin-condensed)",
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  color: "black",
                }}
                href="/"
              >
                <UploadImageContainer
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                >
                  <ArrowBigLeft size={20} />
                  Back
                </UploadImageContainer>
              </Link>
            )}
          </div>
        </div>
        <div
          style={{
            width: "100%",
            height: "calc(100vh - 250px)",
            display: "flex",
            flexDirection: "row",
            gap: "32px",
          }}
        >
          <div
            style={{
              width: "45%",
              height: "100%",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ width: "100%", display: "flex", gap: "8px" }}>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  marginBottom: "12px",
                  boxSizing: "border-box",
                  borderRadius: "0.25rem",
                  color: "#000000",
                  background: "#ccc",
                  cursor: "pointer",
                  fontFamily: "var(--font-cabin-condensed)",
                  fontSize: "1rem",
                  boxShadow: "2px 2px 0px 0px #000000",
                  border: "4px solid #000000",
                  position: "relative",
                }}
              >
                <CustomTab
                  mode={"asset"}
                  currentMode={mode}
                  onClick={() => setMode("asset")}
                >
                  <ImagePlus />
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      fontSize: "12px",
                    }}
                  >
                    Assets
                  </p>
                </CustomTab>
                <CustomTab
                  mode={"draw"}
                  currentMode={mode}
                  onClick={() => setMode("draw")}
                >
                  <Brush />
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      fontSize: "12px",
                    }}
                  >
                    Paint
                  </p>
                </CustomTab>
                <CustomTab
                  mode={"text"}
                  currentMode={mode}
                  onClick={() => setMode("text")}
                >
                  <Type />
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      fontSize: "12px",
                    }}
                  >
                    Text
                  </p>
                </CustomTab>
                <CustomTab
                  mode={"upload"}
                  currentMode={mode}
                  onClick={() => setMode("upload")}
                >
                  <Upload />
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      fontSize: "12px",
                    }}
                  >
                    Upload
                  </p>
                </CustomTab>
              </div>
              <m.div
                whileHover={{
                  scale: 1.02,
                  transition: {
                    duration: 0.15,
                    ease: "linear",
                  },
                }}
                whileTap={{ scale: 0.99 }}
                style={{
                  width: "9rem",
                  display: "flex",
                  flexDirection: "row",
                  marginBottom: "12px",
                  boxSizing: "border-box",
                  borderRadius: "0.25rem",
                  color: "#000000",
                  background: "#ccc",
                  cursor: "pointer",
                  fontFamily: "var(--font-cabin-condensed)",
                  fontSize: "1rem",
                  boxShadow: "2px 2px 0px 0px #000000",
                  border: "4px solid #000000",
                  position: "relative",
                }}
              >
                <button
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: "8px",
                    border: "none",
                    boxSizing: "border-box",
                    color: "#000000",
                    background: "#ccc",
                    cursor: "pointer",
                    fontFamily: "var(--font-cabin-condensed)",
                    fontSize: "1rem",
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  onClick={() => setMode("layers")}
                >
                  <Layers />
                  <p
                    style={{
                      padding: 0,
                      margin: 0,
                      fontSize: "12px",
                    }}
                  >
                    Layers
                  </p>
                </button>
              </m.div>
            </div>
            {(() => {
              switch (mode) {
                case "asset":
                  return (
                    <AssetComponent
                      noProjectFolder={true}
                      noTabs={true}
                      parentVariants={parentVariants}
                      childVariants={childVariants}
                      color={color}
                      assets={[
                        {
                          tab: "head",
                          items: assets[0].assets,
                          icon: assets[0].icon,
                        },
                        {
                          tab: "clothing",
                          items: assets[1].assets,
                          icon: assets[1].icon,
                        },
                        {
                          tab: "face",
                          items: assets[2].assets,
                          icon: assets[2].icon,
                        },
                        {
                          tab: "eye",
                          items: assets[3].assets,
                          icon: assets[3].icon,
                        },
                        {
                          tab: "misc",
                          items: assets[4].assets,
                          icon: assets[4].icon,
                        },
                      ]}
                    />
                  );
                case "draw":
                  return (
                    <DrawComponent
                      parentVariants={parentVariants}
                      undo={undo}
                      redo={redo}
                      color={color}
                    />
                  );
                case "text":
                  return (
                    <TextComponent
                      parentVariants={parentVariants}
                      childVariants={childVariants}
                      color={color}
                    />
                  );
                case "upload":
                  return (
                    <UploadComponent
                      parentVariants={parentVariants}
                      childVariants={childVariants}
                      color={color}
                    />
                  );
                case "layers":
                  return (
                    <div>
                      <LayersComponent
                        parentVariants={parentVariants}
                        color={color}
                      />
                    </div>
                  );
              }
            })()}
          </div>
          <div
            style={{
              height: "100%",
              width: "55%",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "items-start",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: "12px",
                padding: "0px 0px 16px 0px",
                boxSizing: "border-box",
                height: "4rem",
              }}
            >
              <div
                style={{
                  width: "6rem",
                  height: "100%",
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                }}
              >
                <m.button
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: "8px",
                    borderRadius: "0.25rem",
                    boxSizing: "border-box",
                    color: "#000000",
                    background: "#ccc",
                    cursor: "pointer",
                    fontFamily: "var(--font-cabin-condensed)",
                    fontSize: "1rem",
                    boxShadow: "2px 2px 0px 0px #000000",
                    border: "4px solid #000000",
                  }}
                  onClick={() => setFlipImage(!flipImage)}
                >
                  <FlipHorizontal2 />
                </m.button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "12px",
                  height: "100%",
                }}
              >
                <m.div
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                  style={{ width: "6rem", height: "100%" }}
                >
                  <button
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "8px",
                      borderRadius: "0.25rem",
                      boxSizing: "border-box",
                      color: "#000000",
                      background: "#ffffff",
                      cursor: "pointer",
                      fontFamily: "var(--font-cabin-condensed)",
                      fontSize: "1rem",
                      boxShadow: "2px 2px 0px 0px #000000",
                      border: "4px solid #000000",
                    }}
                    onClick={() => {
                      setAssets([]);
                      textFields.forEach((textField) =>
                        removeTextField(textField.id)
                      );
                      setUploadAssets([]);
                      clearCanvas();
                    }}
                  >
                    Clear
                  </button>
                </m.div>
                <m.div
                  whileHover={{
                    scale: 1.02,
                    transition: {
                      duration: 0.15,
                      ease: "linear",
                    },
                  }}
                  whileTap={{ scale: 0.99 }}
                  style={{ width: "8rem", height: "100%" }}
                >
                  <button
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "8px",
                      borderRadius: "0.25rem",
                      boxSizing: "border-box",
                      background: canvasIsLoading ? `${color}80` : color, // Assuming color is in #RRGGBB format
                      color: "#000",
                      cursor: "pointer",
                      fontFamily: "var(--font-cabin-condensed)",
                      fontSize: "1rem",
                      boxShadow: "2px 2px 0px 0px #000000",
                      border: "4px solid #000000",
                    }}
                    onClick={() => {
                      convertToPngAndDisplay();
                      setIsPopupOpen(true);
                    }}
                    disabled={canvasIsLoading}
                  >
                    Download
                  </button>
                </m.div>
              </div>
            </div>
            <MemeCanvas
              mode={mode}
              divRef={divRef}
              canvasRef={canvasRef}
              color={color}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
